import type { Material } from "./types/input";

/**
 * Harçlı / kuru doğal taş duvar malzeme ön ayarları.
 * - Birim hacim ağırlıkları TS 498 Çizelge "Doğal taşlar" (kgf/m³ → kN/m³; harçlı duvar ≈ taşın kendisi: moloz taş duvar 2400 = kalker 2400).
 * - Basınç emniyet gerilmeleri TS 2510 (Nisan 1977) Çizelge 12 (moloz / çekiçle kabaca düzeltilmiş / yönü taşı, λ ≤ 10) ve Çizelge 13
 *   (kesme taş anma değeri), kgf/cm² → ×98,07 kN/m². Taş grupları Çizelge 1 (I kalker–traverten–kireç bağlayıcılı kumtaşı ≥ 350 kgf/cm²,
 *   II yoğun kalker–dolomit–bazalt ≥ 500, III silis bağlayıcılı kumtaşı–grovak ≥ 800, IV granit–siyenit–diyorit–melafir–diyabaz ≥ 1200);
 *   harç grupları Çizelge 2 (A 1:3 çimento, B 1:4 çimento / çimento takviyeli, C–D kireç-çimento (bastard), E kireç harcı).
 * - Kuru duvar (TS 2510 2.6.1.1): yalnız ağırlık istinat ve çevre duvarında; hesap hacim ağırlığı taşın yarısı, bileşke orta 1/3'te
 *   (çekme yok); emniyet gerilmesi tabloda yok → moloz taş + E harcı değeri (en düşük) alınır.
 * - Derz sürtünmesi μ = 0,6, adezyon 0 (taş–harç derzi, KGM/ders pratiği; TS 2510'un τem = 0,1·σb kuralı bina duvarları içindir).
 */
export const STONE_WALL_TYPES = ["kuru", "moloz", "kaba-yonu", "yonu", "kesme"] as const;
export type StoneWallType = (typeof STONE_WALL_TYPES)[number];
export const STONE_GROUPS = ["I", "II", "III", "IV"] as const;
export type StoneGroup = (typeof STONE_GROUPS)[number];
export const MORTAR_GROUPS = ["A", "B", "CD", "E"] as const;
export type MortarGroup = (typeof MORTAR_GROUPS)[number];

export interface StoneKind { id: string; label: string; gamma: number; group: StoneGroup }
export const STONE_KINDS: readonly StoneKind[] = [
  { id: "kalker", label: "Kireçtaşı (kalker), traverten", gamma: 24, group: "I" },
  { id: "kumtasi-kirec", label: "Kireç bağlayıcılı kumtaşı, konglomera", gamma: 26, group: "I" },
  { id: "yogun-kalker", label: "Yoğun kireçtaşı, dolomit", gamma: 27, group: "II" },
  { id: "bazalt", label: "Bazalt", gamma: 30, group: "II" },
  { id: "kumtasi-silis", label: "Silis bağlayıcılı kumtaşı, grovak", gamma: 26, group: "III" },
  { id: "granit", label: "Granit, siyenit, porfir", gamma: 28, group: "IV" },
  { id: "diyabaz", label: "Diyabaz", gamma: 29, group: "IV" },
  { id: "diyorit", label: "Diyorit, melafir, gabro", gamma: 30, group: "IV" },
];

export const STONE_WALL_TYPE_LABELS: Record<StoneWallType, string> = {
  kuru: "Kuru (harçsız) taş duvar",
  moloz: "Harçlı moloz taş duvar",
  "kaba-yonu": "Çekiçle kabaca düzeltilmiş (kaba yonu) taş duvar",
  yonu: "Yönü taşı (ince yonu) duvar",
  kesme: "Kesme taş duvar",
};
export const MORTAR_GROUP_LABELS: Record<MortarGroup, string> = {
  A: "A — 1:3 çimento harcı",
  B: "B — 1:4 çimento harcı (çimento takviyeli)",
  CD: "C–D — kireç-çimento (bastard) harcı",
  E: "E — kireç harcı",
};

/** TS 2510 Çizelge 12 / 13: [I, II, III, IV] kgf/cm² */
const SIGMA_TABLE: Record<Exclude<StoneWallType, "kuru">, Record<MortarGroup, readonly [number, number, number, number]>> = {
  moloz: { E: [3, 4, 5, 6], CD: [3, 4, 6, 8], B: [4, 6, 8, 10], A: [4, 6, 10, 12] },
  "kaba-yonu": { E: [4, 6, 8, 10], CD: [6, 8, 10, 12], B: [8, 10, 12, 15], A: [10, 12, 15, 20] },
  yonu: { E: [4, 6, 8, 10], CD: [8, 10, 12, 15], B: [10, 12, 15, 20], A: [12, 15, 20, 25] },          // E: çizelgede yok → kaba yonu E (tutucu)
  kesme: { E: [4, 6, 8, 10], CD: [10, 15, 20, 25], B: [15, 20, 25, 30], A: [20, 25, 30, 40] },        // Çizelge 13 anma (λ ≤ 10); E yok → kaba yonu E
};
const KGF_CM2 = 98.0665;
const JOINT_FRICTION = 0.6;

export interface StonePreset { wallType: StoneWallType; stone: string; mortar: MortarGroup }

export function stoneKind(id: string): StoneKind {
  return STONE_KINDS.find((k) => k.id === id) ?? STONE_KINDS[0]!;
}

/** TS 2510 emniyet basınç gerilmesi (kN/m²). */
export function stoneAllowableCompression(p: StonePreset): number {
  const k = stoneKind(p.stone);
  const col = STONE_GROUPS.indexOf(k.group);
  const row = p.wallType === "kuru" ? SIGMA_TABLE.moloz.E : SIGMA_TABLE[p.wallType][p.mortar];
  return Math.round(row[col]! * KGF_CM2);
}

export function stonePresetDescription(p: StonePreset): string {
  const k = stoneKind(p.stone);
  const base = `${STONE_WALL_TYPE_LABELS[p.wallType]} — ${k.label} (TS 2510 taş grubu ${k.group})`;
  return p.wallType === "kuru" ? base : `${base}, harç ${MORTAR_GROUP_LABELS[p.mortar]}`;
}

/** Ön ayardan malzeme değerleri: γ (TS 498; kuru duvarda yarısı), σem (TS 2510), μ 0,6, cj 0. */
export function stoneMaterial(p: StonePreset): Material {
  const k = stoneKind(p.stone);
  return {
    gamma: p.wallType === "kuru" ? k.gamma / 2 : k.gamma,
    allowableCompression: stoneAllowableCompression(p),
    jointFriction: JOINT_FRICTION,
    jointCohesion: 0,
    description: stonePresetDescription(p),
    preset: { ...p },
  };
}
